import Avatar from "@shared-models/profile/avatar.model";
import User from "@shared-models/profile/user.model";
import axios from "@shared/axios";
import UserPreferenceInterface from "@shared-interfaces/profile/user.preference.interface";

async function refreshUser(endpoint: string = "/me"): Promise<User>
{
	const response = await axios.get(
		endpoint,
	);

	return response?.data as User ?? null;
}

async function getInfos(): Promise<User>
{
	const response = await axios.get(
		"/profile/infos",
	);

	return response?.data as User ?? null;
}

async function getIdentity(): Promise<User>
{
	const response = await axios.get(
		"/profile/identity",
	);

	return response?.data as User ?? null;
}

async function getEmail(): Promise<User>
{
	const response = await axios.get(
		"/profile/email",
	);

	return response?.data as User ?? null;
}

async function getAddress(): Promise<User>
{
	const response = await axios.get(
		"/profile/address",
	);

	return response?.data as User ?? null;
}

async function getPhoneNumber(): Promise<User>
{
	const response = await axios.get(
		"/profile/phone-number",
	);

	return response?.data as User ?? null;
}

async function getDesktopNumber(): Promise<User>
{
	const response = await axios.get(
		"/profile/desktop-number",
	);

	return response?.data as User ?? null;
}

async function getSocialReason(): Promise<User>
{
	const response = await axios.get(
		"/profile/social-reason",
	);

	return response?.data as User ?? null;
}

async function getEmergency(): Promise<User>
{
	const response = await axios.get(
		"/profile/emergency",
	);

	return response?.data as User ?? null;
}

async function updatePhoto(file: File | null)
{
	const formData = new FormData();
	formData.append("photo", <Blob> file);

	const response = await axios.post(
		"/profile/photo",
		formData,
		{
			headers: {
				"Content-Type": "multipart/form-data"
			}
		}
	);

	return response?.data ?? null;
}

async function updateAvatar(avatar: Avatar)
{
	const response = await axios.put(
		"/profile/avatar",
		{
			"avatar": avatar.id
		}
	);

	return response?.data ?? null;
}

async function deleteAvatar()
{
	const response = await axios.delete(
		"/profile/avatar"
	);

	return response?.data ?? null;
}

async function deletePhoto()
{
	const response = await axios.delete(
		"/profile/photo"
	);

	return response?.data ?? null;
}

async function updateIdentity(user: User)
{
	const response = await axios.put(
		"/profile/identity",
		{
			"firstname": user.firstname,
			"lastname": user.lastname,
			"gender": user.gender,
			"birthdate": user.birthdate,
			"is_student": user.isStudent,
			"is_pwd": user.isPwd,
		}
	);

	return response?.data ?? null;
}

async function sendEmailUpdateCode(email: string)
{
	const response = await axios.put(
		"/profile/email/verify/code",
		{
			"email": email
		}
	);

	return response?.data ?? null;
}

async function sendEmailUpdateToken(email: string)
{
	const response = await axios.put(
		"/profile/email/verify/token",
		{
			"email": email
		}
	);

	return response?.data ?? null;
}

async function updateEmailWithCode(code: string)
{
	const response = await axios.put(
		"/profile/email/code",
		{
			"code": code
		}
	);

	return response?.data ?? null;
}

async function updateEmailWithToken(token: string)
{
	const response = await axios.put(
		"/profile/email/token",
		{
			"token": token
		}
	);

	return response?.data ?? null;
}

async function cancelEmailUpdate()
{
	const response = await axios.put(
		"/profile/email/cancel",
	);

	return response?.data ?? null;
}

async function updatePassword(oldPassword: string, password: string, confirmPassword: string)
{
	const response = await axios.put(
		"/profile/password",
		{
			"old_password": oldPassword,
			"password": password,
			"confirm_password": confirmPassword
		}
	);

	return response?.data ?? null;
}

async function updateAddress(user: User)
{
	const response = await axios.put(
		"/profile/address",
		{
			"address1": user.address1,
			"address2": user.address2,
			"postal_code": user.postalCode,
			"city": user.city,
			"country": user.country
		}
	);

	return response?.data ?? null;
}

async function updatePhoneNumber(user: User)
{
	const response = await axios.put(
		"/profile/phone-number",
		{
			"phone_number": user.phoneNumber
		}
	);

	return response?.data ?? null;
}

async function updateDesktopNumber(user: User)
{
	const response = await axios.put(
		"/profile/desktop-number",
		{
			"desktop_number": user.desktopNumber
		}
	);

	return response?.data ?? null;
}

async function updateSocialReason(user: User)
{
	const response = await axios.put(
		"/profile/social-reason",
		{
			"social_reason": user.socialReason
		}
	);

	return response?.data ?? null;
}

async function updateEmergency(user: User)
{
	const response = await axios.put(
		"/profile/emergency",
		{
			"emergency_name": user.emergencyName,
			"emergency_type": user.emergencyType,
			"emergency_phone_number": user.emergencyPhoneNumber,
		}
	);

	return response?.data ?? null;
}

async function updatePreferences(keyToUpdate: keyof UserPreferenceInterface, value: any)
{
	const response = await axios.put(
		"/profile/preferences",
		{
			key: keyToUpdate,
			value: value
		}
	);

	return response?.data ?? null;
}

async function updatePersonalSituation(user: User)
{
	const response = await axios.put(
		"/profile/personal-situation",
		{
			"is_student": !! user.isStudent,
			"is_pwd": !! user.isPwd,
		}
	);

	return response?.data ?? null;
}

async function sendTestEmail(): Promise<string>
{
	const response = await axios.post("/profile/email/send-test");
	return response?.data?.message ?? "E-mail de test envoyé";
}

async function getBillingPortalUrl(): Promise<string | null>
{
	const response = await axios.get("/profile/billing-portal", {
		data: { skipLoading: true }
	});

	return response?.data?.url ?? null;
}

export {
	refreshUser,
	getInfos,
	getIdentity,
	getEmail,
	getAddress,
	getPhoneNumber,
	getDesktopNumber,
	getSocialReason,
	getEmergency,

	updatePhoto,
	updateAvatar,
	deleteAvatar,
	deletePhoto,
	updateIdentity,
	sendEmailUpdateCode,
	sendEmailUpdateToken,
	updateEmailWithCode,
	updateEmailWithToken,
	cancelEmailUpdate,
	updatePassword,
	updateAddress,
	updatePhoneNumber,
	updateDesktopNumber,
	updateSocialReason,
	updateEmergency,
	updatePreferences,
	updatePersonalSituation,
	sendTestEmail,
	getBillingPortalUrl
};
